import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { oportunidadeService } from '@/services/oportunidadeService'
import { pendenciaService } from '@/services/pendenciaService'
import { Oportunidade } from '@/types/oportunidade'
import { Pendencia, PendenciaStatus } from '@/types/pendencia'
import { ETAPA_COMERCIAL_LABEL } from '@/types/statusReserva'
import { formatCNPJ, formatDate } from '@/utils/formatters'
import { cnpjContemTermo } from '@/utils/cnpj'
import { tokens } from '@/theme'
import {
  FiltroConsultor,
  TODOS_CONSULTORES,
  consultoresDasReservas,
} from '@/components/common/FiltroConsultor'

/** Status 360 (Figma "Operações e Clientes"): três estados, derivados — nunca gravados. */
export type Status360 = 'EM_DIA' | 'ATENCAO' | 'CRITICO'

export const STATUS_360_LABEL: Record<Status360, string> = {
  EM_DIA: 'Em dia',
  ATENCAO: 'Atenção',
  CRITICO: 'Crítico',
}

/** Mesma janela da renovação e do filtro de vigência de Minhas Reservas (RES-003). */
export const JANELA_CRITICA_DIAS = 5

/** Pendência que ainda espera resposta do consultor (mesmo conjunto de "Responder →"). */
const PENDENCIA_ABERTA: PendenciaStatus[] = ['ABERTA', 'VISUALIZADA', 'REABERTA']

/** Status pós-assinatura em que o prazo de operacionalização é o que vale (Minhas Reservas). */
const STATUS_EM_OPERACIONALIZACAO = ['CONTRATO_ASSINADO', 'EM_OPERACIONALIZACAO']

/** Depois de ATIVO ou nos terminais nenhum prazo alerta: a reserva já chegou onde precisava. */
const STATUS_SEM_PRAZO = ['ATIVO', 'ENCERRADA', 'CANCELADA', 'NAO_CONVERTIDA']

/** Dias inteiros (meia-noite local) até um prazo `yyyy-MM-dd`; negativo = vencido. */
const diasAte = (prazoISO: string | null | undefined, hoje: Date): number | null => {
  if (!prazoISO) return null
  const base = new Date(hoje)
  base.setHours(0, 0, 0, 0)
  return Math.round((new Date(`${prazoISO}T00:00:00`).getTime() - base.getTime()) / 86_400_000)
}

/** Qual prazo governa a reserva agora (a mesma regra de `prazoVigente` em Minhas Reservas). */
const prazoQueGoverna = (o: Oportunidade): string | null => {
  const status = o.status ?? null
  if (status && STATUS_EM_OPERACIONALIZACAO.includes(status)) return o.prazoOperacionalizacao ?? null
  if (status && STATUS_SEM_PRAZO.includes(status)) return null
  if (!status && o.estagio === 'CONTRATADA') return null
  if (o.etapaComercial === 'GANHO' || o.etapaComercial === 'PERDIDO') return null
  return o.prazoReserva ?? null
}

export interface Avaliacao360 {
  status: Status360
  /** Por que está nesse estado — vai para o tooltip e para a linha de apoio do chip. */
  motivo: string
}

/**
 * Regra do Status 360 (definição de pronto do A16), só com campos que a lista já entrega:
 *  - Crítico  = prazo que governa a reserva vencido OU vencendo em até 5 dias;
 *  - Atenção  = pendência aberta esperando o consultor (sem prazo crítico);
 *  - Em dia   = nada disso.
 * Não existe endpoint consolidado por empresa (lacuna relatada): a derivação é da tela,
 * a partir de `GET /v1/oportunidades` + `GET /v1/pendencias/minhas`.
 */
export const avaliarStatus360 = (
  o: Oportunidade,
  pendenciasAbertas: number,
  hoje: Date = new Date(),
): Avaliacao360 => {
  const prazo = prazoQueGoverna(o)
  const dias = diasAte(prazo, hoje)
  if (dias !== null && dias < 0) {
    return { status: 'CRITICO', motivo: `Prazo vencido há ${Math.abs(dias)} dia(s) (${formatDate(prazo)})` }
  }
  if (dias !== null && dias <= JANELA_CRITICA_DIAS) {
    return {
      status: 'CRITICO',
      motivo: dias === 0 ? `Prazo vence hoje (${formatDate(prazo)})` : `Prazo vence em ${dias} dia(s) (${formatDate(prazo)})`,
    }
  }
  if (pendenciasAbertas > 0) {
    return { status: 'ATENCAO', motivo: `${pendenciasAbertas} pendência(s) aguardando sua resposta` }
  }
  return { status: 'EM_DIA', motivo: 'Sem pendência aberta nem prazo vencido' }
}

const COR_CHIP: Record<Status360, 'success' | 'warning' | 'error'> = {
  EM_DIA: 'success',
  ATENCAO: 'warning',
  CRITICO: 'error',
}

export interface Linha360 {
  id: string
  empresa: string
  cnpj: string
  consultorId: string | null
  /** Nome gravado na reserva (V49); "—" em reserva antiga sem nome conhecido. */
  consultor: string
  produto: string
  parceiro: string
  etapa: string
  entradaEm: string
  ultimaMovimentacao: string
  avaliacao: Avaliacao360
}

type Filtro360 = 'TODOS' | Status360

interface Props {
  /** Renderizada como aba de "Operações e Clientes" (cabeçalho e KPIs vêm de lá). */
  embutida?: boolean
}

/**
 * Visão 360 (Onda 8 / A16): uma linha por reserva/empresa da carteira com o Status 360
 * derivado. Empresa/CNPJ · Produto · Parceiro · Entrada · Última movimentação · Status 360.
 *
 * "Última movimentação" cai em `createdAt` quando a listagem não traz `updatedAt`
 * (`OportunidadeResumoResponse` não tem o campo — lacuna relatada, mesma de Minhas Reservas).
 */
const Visao360Page: FC<Props> = ({ embutida = false }) => {
  const navigate = useNavigate()
  const [oportunidades, setOportunidades] = useState<Oportunidade[]>([])
  const [pendencias, setPendencias] = useState<Pendencia[]>([])
  const [pendenciasIndisponiveis, setPendenciasIndisponiveis] = useState(false)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<Filtro360>('TODOS')
  const [consultorId, setConsultorId] = useState<string>(TODOS_CONSULTORES)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    setErroStatus(null)
    try {
      const [pageOp, minhas] = await Promise.all([
        oportunidadeService.listar(0, 200),
        // Sem as pendências ainda dá para avaliar o prazo; o chip avisa que a
        // dimensão "pendências" ficou de fora em vez de afirmar "Em dia".
        pendenciaService.minhas().catch(() => null),
      ])
      setOportunidades(pageOp.content ?? [])
      setPendencias(minhas ?? [])
      setPendenciasIndisponiveis(minhas === null)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar a carteira')
      setErroStatus((e as { status?: number })?.status ?? null)
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const linhas = useMemo<Linha360[]>(() => {
    const abertasPorReserva = new Map<string, number>()
    for (const p of pendencias) {
      if (!PENDENCIA_ABERTA.includes(p.status)) continue
      abertasPorReserva.set(p.oportunidadeId, (abertasPorReserva.get(p.oportunidadeId) ?? 0) + 1)
    }
    const hoje = new Date()
    return oportunidades.map((o) => ({
      id: o.id,
      empresa: o.razaoSocial ?? o.nomeFantasia ?? `Reserva ${String(o.numero ?? o.id).slice(0, 8)}`,
      cnpj: o.cnpj ? formatCNPJ(o.cnpj) : '—',
      consultorId: o.usuarioResponsavel ?? null,
      consultor: o.consultorNome ?? '—',
      produto: o.tipoNecessidade ?? '—',
      parceiro: o.parceiroSelecionadoNome ?? '—',
      etapa: o.etapaComercial ? ETAPA_COMERCIAL_LABEL[o.etapaComercial] : '—',
      entradaEm: formatDate(o.createdAt),
      ultimaMovimentacao: formatDate(o.updatedAt ?? o.createdAt),
      avaliacao: avaliarStatus360(o, abertasPorReserva.get(o.id) ?? 0, hoje),
    }))
  }, [oportunidades, pendencias])

  const opcoesConsultor = useMemo(() => consultoresDasReservas(oportunidades), [oportunidades])

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    return linhas.filter(
      (l) =>
        (filtro === 'TODOS' || l.avaliacao.status === filtro) &&
        (consultorId === TODOS_CONSULTORES || l.consultorId === consultorId) &&
        (!termo ||
          l.empresa.toLowerCase().includes(termo) ||
          cnpjContemTermo(l.cnpj, termo) ||
          l.produto.toLowerCase().includes(termo) ||
          l.parceiro.toLowerCase().includes(termo) ||
          l.consultor.toLowerCase().includes(termo)),
    )
  }, [linhas, busca, filtro, consultorId])

  const contagem = useMemo(() => {
    const c: Record<Status360, number> = { EM_DIA: 0, ATENCAO: 0, CRITICO: 0 }
    for (const l of linhas) c[l.avaliacao.status] += 1
    return c
  }, [linhas])

  const colunas: ColunaTabelaAcao<Linha360>[] = [
    {
      titulo: 'Empresa / CNPJ',
      render: (l) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.empresa}</Typography>
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>{l.cnpj}</Typography>
        </Box>
      ),
    },
    { titulo: 'Consultor', render: (l) => l.consultor },
    { titulo: 'Produto', render: (l) => l.produto },
    { titulo: 'Parceiro', render: (l) => l.parceiro },
    { titulo: 'Etapa', render: (l) => l.etapa },
    { titulo: 'Entrada', render: (l) => l.entradaEm },
    { titulo: 'Última movimentação', render: (l) => l.ultimaMovimentacao },
    {
      titulo: 'Status 360',
      render: (l) => (
        <Tooltip title={l.avaliacao.motivo}>
          <Chip
            size="small"
            color={COR_CHIP[l.avaliacao.status]}
            label={STATUS_360_LABEL[l.avaliacao.status]}
            data-status360={l.avaliacao.status}
          />
        </Tooltip>
      ),
    },
  ]

  return (
    <Box>
      {!embutida && <Typography variant="h5" sx={{ mb: 2 }}>Visão 360</Typography>}

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por empresa, CNPJ, produto, parceiro ou consultor…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 240, maxWidth: 420 }}
          inputProps={{ 'aria-label': 'Buscar na carteira' }}
        />
        <FiltroConsultor opcoes={opcoesConsultor} valor={consultorId} onChange={setConsultorId} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v: Filtro360 | null) => v && setFiltro(v)}
          aria-label="Filtrar por Status 360"
        >
          <ToggleButton value="TODOS">Todos</ToggleButton>
          <ToggleButton value="EM_DIA">Em dia ({contagem.EM_DIA})</ToggleButton>
          <ToggleButton value="ATENCAO">Atenção ({contagem.ATENCAO})</ToggleButton>
          <ToggleButton value="CRITICO">Crítico ({contagem.CRITICO})</ToggleButton>
        </ToggleButtonGroup>
        {pendenciasIndisponiveis && !carregando && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Pendências indisponíveis — Status 360 só pelo prazo"
          />
        )}
      </Stack>

      {carregando ? (
        <EstadoDeLista estado="carregando" mensagem="Carregando carteira…" />
      ) : erro ? (
        <EstadoDeLista
          estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
          mensagem={erroStatus === 403 ? undefined : 'Não foi possível carregar a carteira'}
          detalhe={erroStatus === 403 ? undefined : erro}
          acao={erroStatus === 403 ? undefined : { label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : linhas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma empresa na carteira"
          detalhe="As empresas aparecem aqui a partir da primeira reserva."
          acao={{ label: 'Nova oportunidade', onClick: () => navigate('/reservas/nova') }}
        />
      ) : filtradas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma empresa com esses filtros"
          detalhe="Ajuste a busca, o consultor ou o Status 360."
        />
      ) : (
        <TabelaAcao<Linha360>
          aria-label="Visão 360 da carteira"
          colunas={colunas}
          linhas={filtradas}
          chaveDaLinha={(l) => l.id}
          onAbrir={(l) => navigate(`/reservas/${l.id}/estruturacao`)}
        />
      )}
    </Box>
  )
}

export default Visao360Page
